import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import KeyData from "@/components/KeyData";
import Term from "@/components/Term";
import TodaysPractice from "@/components/TodaysPractice";
import CelestialLoader from "@/components/CelestialLoader";
import { PlanetName, NakshatraName } from "@/components/PlanetName";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { ArrowRight, Flame } from "lucide-react";

export default function Today() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, lang } = useLang();
  const [data, setData] = useState(null);
  const [streak, setStreak] = useState(0);

  useEffect(() => {
    api.get("/dashboard/today").then(r => setData(r.data)).catch(() => {});
  }, [lang]);

  if (!data) return <CelestialLoader />;

  const today = new Date(data.today_date);
  const locale = lang === "hi" ? "hi-IN" : "en-IN";
  const dateLong = today.toLocaleDateString(locale, { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  const chapter = data.current_chapter;
  const trialDaysLeft = user?.trial_ends_at ? Math.max(0, Math.ceil((new Date(user.trial_ends_at) - new Date()) / (1000*60*60*24))) : null;

  return (
    <div className="animate-gentle-rise">
      <header className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="font-display text-h1 leading-tight" data-testid="today-date">{dateLong}</h1>
          <p className="text-caption mt-1" style={{ color: "var(--vedari-ink-soft)" }}>
            {t("today.day_of_chapter", { n: chapter.days_walked })}
          </p>
        </div>
        <div className="flex flex-col items-end gap-1.5">
          {trialDaysLeft !== null && trialDaysLeft > 0 && (
            <p className="text-caption px-3 py-1 rounded-full" style={{ backgroundColor: "rgba(184,149,74,0.10)", color: "var(--vedari-gold)" }} data-testid="trial-indicator">
              {t("today.trial")} · <KeyData>{trialDaysLeft}</KeyData> {t("today.days")}
            </p>
          )}
          {streak > 0 && (
            <span className="inline-flex items-center gap-1 text-caption" style={{ color: "var(--vedari-gold)" }} data-testid="today-streak-badge">
              <Flame size={12} strokeWidth={1.75} />
              {t("practice.streak", { n: streak })}
            </span>
          )}
        </div>
      </header>

      {/* Today's Practice (new prominent section) */}
      <TodaysPractice onStreakChange={setStreak} />

      {/* Card 1: Today's Guidance */}
      <section className="vedari-card mb-6" data-testid="today-guidance-card">
        <p className="text-caption tracking-wider uppercase" style={{ color: "var(--vedari-ink-soft)" }}>
          <Term k="nakshatra">{t("today.nakshatra")}</Term>
        </p>
        <h2 className="font-serif text-[28px] mt-1" style={{ color: "var(--vedari-gold)" }}>
          <KeyData><NakshatraName name={data.nakshatra.name} /></KeyData>
        </h2>
        <p className="text-body-lg mt-2 leading-relaxed font-serif italic" style={{ color: "var(--vedari-ink-soft)" }}>
          {data.nakshatra.theme}
        </p>
        {data.daily.personal_note && (
          <p className="text-body mt-4 leading-relaxed" data-testid="today-personal-note">
            {data.daily.personal_note}
          </p>
        )}

        <div className="ornate-divider" />

        <p className="text-caption tracking-wider uppercase" style={{ color: "var(--vedari-ink-soft)" }}>{t("today.within_mission")}</p>
        <p className="text-body-lg mt-2 leading-relaxed">{data.daily.mission_action}</p>

        <p className="text-body-sm mt-5 pt-4 font-serif italic leading-relaxed" style={{ borderTop: "1px solid var(--vedari-divider)", color: "var(--vedari-ink-soft)" }}>
          {t("today.watch")} — {data.daily.guidance}
        </p>
      </section>

      {/* Card 2: Reflection */}
      <section className="vedari-card mb-6" data-testid="today-reflection-card">
        <p className="text-caption tracking-wider uppercase" style={{ color: "var(--vedari-gold)" }}>{t("today.reflection")}</p>
        <p className="font-serif text-h3 mt-3 leading-relaxed">{data.daily.reflection_question}</p>
        <button
          onClick={() => document.querySelector('[data-testid="add-to-journey-fab"]')?.click()}
          className="vedari-btn-secondary mt-5 w-full"
          data-testid="today-answer-journal-btn"
        >
          {t("today.answer_journal")}
        </button>
      </section>

      {/* Card 3: Chapter Progress Peek — now clickable to Antardasha detail */}
      <button
        onClick={() => navigate(`/app/journey/antardasha-current`)}
        className="vedari-card mb-6 w-full text-left transition-all"
        data-testid="today-chapter-card"
      >
        <p className="text-caption uppercase tracking-wider" style={{ color: "var(--vedari-ink-soft)" }}>
          <Term k="antardasha">{t("today.current_antardasha")}</Term>
        </p>
        <h3 className="font-serif text-h2 mt-1">
          <KeyData><PlanetName planet={chapter.mahadasha} /></KeyData>–<KeyData><PlanetName planet={chapter.antardasha} /></KeyData>
        </h3>
        <p className="text-body mt-3">{t("today.days_remaining", { n: chapter.days_remaining })}</p>
        <div className="h-1.5 rounded-full mt-3 overflow-hidden" style={{ backgroundColor: "var(--vedari-divider)" }}>
          <div className="h-full transition-all" style={{ width: `${chapter.progress_pct}%`, backgroundColor: "var(--vedari-gold)" }} />
        </div>
        <span className="text-body-sm mt-5 inline-flex items-center gap-1" style={{ color: "var(--vedari-slate)" }}>
          {t("today.open_mission")} <ArrowRight size={14} />
        </span>
      </button>

      {/* Card 4: From Your Journal */}
      <section className="vedari-card mb-8" data-testid="today-journal-card">
        <p className="text-caption uppercase tracking-wider mb-3" style={{ color: "var(--vedari-ink-soft)" }}>{t("today.from_journal")}</p>
        {data.latest_annals?.length ? (
          <div className="space-y-4">
            {data.latest_annals.map((e) => (
              <div key={e.entry_id} className="pb-3 last:pb-0" style={{ borderBottom: "1px solid var(--vedari-divider)" }}>
                <p className="text-caption" style={{ color: "var(--vedari-gold)" }}><KeyData>{formatDateShort(e.created_at, locale)}</KeyData></p>
                <p className="text-body mt-1 line-clamp-2 leading-relaxed">{e.content}</p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-body-sm font-serif italic" style={{ color: "var(--vedari-ink-soft)" }}>
            {t("today.empty_journal")}
          </p>
        )}
        <button onClick={() => navigate("/app/journey/journal")} className="text-body-sm mt-5 inline-flex items-center gap-1" style={{ color: "var(--vedari-slate)" }} data-testid="today-open-journal-btn">
          {t("today.open_journal")} <ArrowRight size={14} />
        </button>
      </section>
    </div>
  );
}

function formatDateShort(iso, locale = "en-IN") {
  const d = new Date(iso);
  return d.toLocaleDateString(locale, { day: "numeric", month: "short" });
}
